import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { login, getGoogleLoginUrl, buildShopifyAdminAppUrl, forgotPassword, handleLaravelRedirect } from '../api/auth';
import aerochatLogo from '../assets/aerochat_logo_full.svg';
import '../styles/pages/login.css';

const LoginPage = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  // View: 'main' = email/social login, 'shopify' = store URL input, 'forgot' = reset password
  const [view, setView] = useState('main');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // A `redirect` query param lets a deep-link (e.g. the AppSumo "log in to
  // attach your license" flow) land on a specific page after login. Only
  // same-origin internal paths are allowed (must start with a single "/")
  // to prevent open-redirects to an external site.
  const getSafeRedirect = () => {
    const r = searchParams.get('redirect');
    return r && /^\/(?!\/)/.test(r) ? r : null;
  };

  // Show error from OAuth redirect (e.g. ?error=google_auth_failed)
  useEffect(() => {
    const oauthError = searchParams.get('error');
    if (oauthError === 'google_auth_failed') {
      setError(t('login.validation.googleAuthFailed'));
      // Clean the URL so the error doesn't persist on refresh
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Shopify store URL
  const [storeUrl, setStoreUrl] = useState('');
  const [shopifyError, setShopifyError] = useState('');

  // Forgot password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError(t('login.validation.enterEmailAndPassword'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await login(email, password);
      const safeRedirect = getSafeRedirect();
      // AppSumo attach: a legacy user (use_react_admin=0) must reach the React
      // attach page to activate — but the normal Laravel redirect would bounce
      // them away first. When heading to the attach page, stay in React; the
      // attach then flips use_react_admin=1 so future logins land on React.
      const goingToAttach = !!safeRedirect && safeRedirect.startsWith('/appsumo/attach');
      if (!goingToAttach && handleLaravelRedirect(data)) return;
      onLoginSuccess(data.user);
      // Honor a safe internal ?redirect= target (e.g. the AppSumo attach flow).
      if (safeRedirect) {
        navigate(safeRedirect, { replace: true });
      }
    } catch (err) {
      setError(err.message || t('login.validation.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = getGoogleLoginUrl();
  };

  const handleShopifySubmit = (e) => {
    e.preventDefault();
    setShopifyError('');

    const url = storeUrl.trim().toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/+$/, '');

    if (!url.includes('.myshopify.com')) {
      setShopifyError(t('shopify.validation.invalidStoreUrl'));
      return;
    }

    // Extract store name: "ikea-aerochat.myshopify.com" → "ikea-aerochat"
    const storeName = url.split('.myshopify.com')[0];
    if (!storeName) {
      setShopifyError(t('shopify.validation.invalidStoreUrl'));
      return;
    }

    // Hands the merchant to their Shopify admin, where opening the embedded
    // app IS the session — there is no OAuth round trip to run any more.
    const adminUrl = buildShopifyAdminAppUrl(storeName);
    if (!adminUrl) {
      setShopifyError(t('shopify.validation.appUnavailable'));
      return;
    }
    window.location.href = adminUrl;
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      setForgotError(t('forgot.validation.enterEmail'));
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      await forgotPassword(forgotEmail.trim());
      setForgotSent(true);
    } catch (err) {
      setForgotError(err.message || t('forgot.validation.sendFailed'));
    } finally {
      setForgotLoading(false);
    }
  };

  // ─── Forgot Password View ───
  if (view === 'forgot') {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-card__header">
            {forgotSent ? (
              <>
                <div className="login-card__icon login-card__icon--success">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                </div>
                <h1 className="login-card__title">{t('forgot.checkEmailTitle')}</h1>
                <p className="login-card__subtitle" dangerouslySetInnerHTML={{ __html: t('forgot.checkEmailSubtitle', { email: forgotEmail, interpolation: { escapeValue: true } }) }} />
              </>
            ) : (
              <>
                <div className="login-card__icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2" strokeLinecap="round"><rect x="3" y="5" width="18" height="14" rx="2"/><polyline points="3 7 12 13 21 7"/></svg>
                </div>
                <h1 className="login-card__title">{t('forgot.title')}</h1>
                <p className="login-card__subtitle">{t('forgot.subtitle')}</p>
              </>
            )}
          </div>

          {forgotSent ? (
            <div className="login-card__form">
              <button
                type="button"
                className="login-card__submit"
                onClick={() => { setView('main'); setForgotEmail(''); setForgotSent(false); setForgotError(''); }}
              >
                {t('forgot.backToSignIn')}
              </button>
              <button
                type="button"
                className="login-card__resend-btn"
                onClick={() => { setForgotSent(false); }}
              >
                {t('forgot.resend')}
              </button>
            </div>
          ) : (
            <form className="login-card__form" onSubmit={handleForgotSubmit}>
              {forgotError && (
                <div className="login-card__error">{forgotError}</div>
              )}
              <div className="login-card__field">
                <label className="login-card__label">{t('forgot.emailLabel')}</label>
                <input
                  type="email"
                  className="login-card__input"
                  placeholder={t('forgot.emailPlaceholder')}
                  value={forgotEmail}
                  onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                  autoFocus
                />
              </div>
              <button type="submit" className="login-card__submit" disabled={forgotLoading}>
                {forgotLoading ? t('forgot.sending') : t('forgot.resetPassword')}
              </button>
            </form>
          )}

          {!forgotSent && (
            <button
              type="button"
              className="login-card__back-btn"
              onClick={() => { setView('main'); setForgotEmail(''); setForgotError(''); }}
            >
              {t('forgot.backLink')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Shopify Store URL View ───
  if (view === 'shopify') {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-card__header">
            <h1 className="login-card__title">{t('shopify.title')}</h1>
            <p className="login-card__subtitle">{t('shopify.subtitle')}</p>
          </div>

          <form className="login-card__form" onSubmit={handleShopifySubmit}>
            <div className="login-card__field">
              <label className="login-card__label">{t('shopify.storeUrlLabel')}</label>
              <input
                type="text"
                className="login-card__input"
                placeholder={t('shopify.storeUrlPlaceholder')}
                value={storeUrl}
                onChange={(e) => { setStoreUrl(e.target.value); setShopifyError(''); }}
                autoFocus
              />
              {shopifyError && (
                <span className="login-card__field-error">{shopifyError}</span>
              )}
              <span className="login-card__field-hint">{t('shopify.storeUrlHint')}</span>
            </div>

            <button type="submit" className="login-card__submit">
              {t('shopify.continue')}
            </button>
          </form>

          <button
            type="button"
            className="login-card__back-btn"
            onClick={() => { setView('main'); setStoreUrl(''); setShopifyError(''); }}
          >
            {t('shopify.backToSignIn')}
          </button>

          <p className="login-card__footer">
            {t('login.termsText')}{' '}
            <a href="https://aerochat.ai/legal/terms" target="_blank" rel="noopener noreferrer" className="login-card__link">{t('login.termsLink')}</a> {t('login.and')}{' '}
            <a href="https://aerochat.ai/legal/privacy" target="_blank" rel="noopener noreferrer" className="login-card__link">{t('login.privacyLink')}</a>
          </p>
        </div>
      </div>
    );
  }

  // ─── Main Login View ───
  return (
    <div className="login-page">
      <div className="login-card">
        {/* Logo */}
        <div className="login-card__logo">
          <img src={aerochatLogo} alt="AeroChat" className="login-card__logo-img" />
        </div>

        <div className="login-card__header">
          <h1 className="login-card__title">{t('login.title')}</h1>
          <p className="login-card__subtitle">{t('login.subtitle')}</p>
        </div>

        {/* Social Login Buttons */}
        <div className="login-card__social">
          <button type="button" className="login-card__social-btn" onClick={handleGoogleLogin}>
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            {t('login.continueWithGoogle')}
          </button>

          <button type="button" className="login-card__social-btn" onClick={() => setView('shopify')}>
            <svg width="20" height="20" viewBox="0 0 256 292" xmlns="http://www.w3.org/2000/svg">
              <path d="M223.774 57.34c-.201-1.46-1.48-2.268-2.537-2.357-1.055-.088-23.383-1.743-23.383-1.743s-15.507-15.395-17.209-17.099c-1.703-1.703-5.029-1.185-6.32-.805-.19.056-3.388 1.043-8.678 2.68-5.18-14.906-14.322-28.604-30.405-28.604-.444 0-.901.018-1.358.044C129.31 3.407 123.644.779 118.75.779c-37.465 0-55.364 46.835-60.952 70.634-14.558 4.514-24.826 7.7-26.207 8.14-8.126 2.549-8.383 2.805-9.45 10.462C21.3 96.576.221 260.912.221 260.912l167.86 31.3 90.917-19.81s-35.02-213.632-35.224-215.062zM168.603 43.209c-4.15 1.29-8.886 2.756-13.986 4.34-.045-7.237-.625-17.313-2.635-25.654 10.39 2.062 15.465 13.706 16.62 21.314zM143.22 51.102c-9.472 2.938-19.832 6.15-30.22 9.372 5.83-22.41 16.72-33.234 26.23-37.326 3.713 7.226 4.07 17.49 3.99 27.954zM118.997 7.81c1.702 0 3.18.38 4.58 1.188-9.932 4.673-20.6 16.454-25.117 39.942-8.271 2.563-16.381 5.083-23.862 7.397C81.11 31.899 96.445 7.81 118.997 7.81z" fill="#95BF47"/>
              <path d="M221.237 54.983c-1.055-.088-23.383-1.743-23.383-1.743s-15.507-15.395-17.209-17.099c-.637-.634-1.496-.959-2.394-1.099l-12.527 255.57 90.917-19.81S221.62 56.444 221.42 54.983h-.183z" fill="#5E8E3E"/>
              <path d="M135.242 104.585l-11.069 32.926s-9.698-5.176-21.586-5.176c-17.428 0-18.305 10.937-18.305 13.693 0 15.038 39.2 20.8 39.2 56.024 0 27.713-17.577 45.558-41.277 45.558-28.44 0-42.984-17.7-42.984-17.7l7.615-25.16s14.95 12.835 27.565 12.835c8.243 0 11.596-6.49 11.596-11.232 0-19.616-32.16-20.491-32.16-52.724 0-27.129 19.472-53.382 58.778-53.382 15.145 0 22.627 4.338 22.627 4.338z" fill="#FFF"/>
            </svg>
            {t('login.continueWithShopify')}
          </button>
        </div>

        {/* Divider */}
        <div className="login-card__divider">
          <span className="login-card__divider-line" />
          <span className="login-card__divider-text">{t('login.orSignInWithEmail')}</span>
          <span className="login-card__divider-line" />
        </div>

        {/* Email/Password Form */}
        <form className="login-card__form" onSubmit={handleSubmit}>
          {error && (
            <div className="login-card__error">{error}</div>
          )}

          <div className="login-card__field">
            <label className="login-card__label">{t('login.emailLabel')}</label>
            <input
              type="email"
              className="login-card__input"
              placeholder={t('login.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>

          <div className="login-card__field">
            <label className="login-card__label">{t('login.passwordLabel')}</label>
            <div className="login-card__input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                className="login-card__input login-card__input--password"
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button type="button" className="login-card__eye-btn" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <button type="button" className="login-card__forgot" onClick={() => { setView('forgot'); setForgotEmail(email); }}>{t('login.forgotPassword')}</button>

          <button
            type="submit"
            className="login-card__submit"
            disabled={loading}
          >
            {loading ? t('login.signingIn') : t('login.signIn')}
          </button>
        </form>

        {/* Footer */}
        <p className="login-card__footer">
          {t('login.noAccount')}{' '}
          <button
            type="button"
            className="login-card__link"
            onClick={() => navigate('/signup')}
            style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
          >
            {t('login.signUp')}
          </button>
        </p>

        <p className="login-card__footer" style={{ marginTop: 8 }}>
          {t('login.termsText')}{' '}
          <a href="https://aerochat.ai/legal/terms" target="_blank" rel="noopener noreferrer" className="login-card__link">{t('login.termsLink')}</a> {t('login.and')}{' '}
          <a href="https://aerochat.ai/legal/privacy" target="_blank" rel="noopener noreferrer" className="login-card__link">{t('login.privacyLink')}</a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
